import { isDefined } from "@clipboard-health/util-ts";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useEnableDistanceFiltering } from "@src/appV2/ShiftBlocks/hooks/useEnableDistanceFiltering";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath, useHistory } from "react-router-dom";

import { useWorkerPreferences } from "../../../Agents/api/useWorkerPreferences";
import { RootPaths } from "../../../App/paths";
import { useCreateChatChannel } from "../../../Facilities/Chat/api/useCreateChatChannel";
import { useToast } from "../../../lib";
import { useFetchPaginatedShiftBlocks } from "../../../ShiftBlocks/api/useFetchPaginatedShiftBlocks";
import { LoadingButton } from "../../components/LoadingButton";
import {
  SHIFT_DISCOVERY_LIST_PATH,
  SHIFT_DISCOVERY_ORIENTATION_BLOCKS_PATH,
} from "../../ShiftDiscovery/paths";
import { useFutureShifts } from "../../WorkerShifts/api/useFutureShifts";
import { type ModalWorkplaceData } from "../types";
import { ShiftBottomSheetBookabilityCriteriaCard } from "./BookabilityCriteriaCard";

interface BaseOrientationRequiredCardProps {
  worker: Worker;
  workplace: ModalWorkplaceData;
  shiftQualification: string;
  shiftId: string;
  shiftHasAdditionalUnmetBookabilityCriteria: boolean;
}

interface OrientationRequiredCardTextInputs {
  workplaceHasUpcomingOrientation: boolean;
  upcomingOrientationAtWorkplace: boolean;
  shiftHasAdditionalUnmetBookabilityCriteria: boolean;
}

function getButtonText(inputs: OrientationRequiredCardTextInputs) {
  const {
    workplaceHasUpcomingOrientation,
    shiftHasAdditionalUnmetBookabilityCriteria,
    upcomingOrientationAtWorkplace,
  } = inputs;

  if (upcomingOrientationAtWorkplace) {
    return undefined;
  }

  if (shiftHasAdditionalUnmetBookabilityCriteria) {
    if (workplaceHasUpcomingOrientation) {
      return "View Orientation Blocks";
    }

    return undefined;
  }

  if (workplaceHasUpcomingOrientation) {
    return "Book Orientation Block";
  }

  return "Request Availability";
}

function getDescription(inputs: OrientationRequiredCardTextInputs) {
  const {
    shiftHasAdditionalUnmetBookabilityCriteria,
    upcomingOrientationAtWorkplace,
    workplaceHasUpcomingOrientation,
  } = inputs;
  if (shiftHasAdditionalUnmetBookabilityCriteria) {
    return "Complete all other workplace requirements to book an orientation block.";
  }

  if (upcomingOrientationAtWorkplace) {
    return "You have already scheduled an orientation block at this workplace. Complete it without cancelling to unlock access!";
  }

  if (workplaceHasUpcomingOrientation) {
    return "Complete the workplace's orientation block to unlock access!";
  }

  return "Complete the workplace's orientation block to unlock access";
}

export function BaseOrientationRequiredCard(props: BaseOrientationRequiredCardProps) {
  const {
    workplace,
    worker,
    shiftQualification,
    shiftId,
    shiftHasAdditionalUnmetBookabilityCriteria,
  } = props;
  const { filters } = useWorkerPreferences();
  const { enableDistanceFiltering } = useEnableDistanceFiltering();
  const { userId: workerId } = useDefinedWorker();

  const facilityId = workplace.id;
  const agentId = worker.userId;
  const history = useHistory();
  const { mutateAsync: createChatChannel, isLoading: isCreatingChatChannel } =
    useCreateChatChannel();
  const { showErrorToast } = useToast();

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_ORIENTATION_REQUIRED_CARD, {
    shiftId,
    workerId,
  });

  const handleRouteToShiftBlocksView = () => {
    const path = generatePath(
      `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}/${SHIFT_DISCOVERY_ORIENTATION_BLOCKS_PATH}`,
      {
        facilityId,
      }
    );

    const params = new URLSearchParams({
      qualification: shiftQualification,
      shiftIdWithOrientationRequired: shiftId,
    });

    history.push({
      pathname: path,
      search: `?${params.toString()}`,
    });

    logEvent(APP_V2_USER_EVENTS.ORIENTATION_BOOK_SHIFTS_TAPPED, {
      shiftId,
      workplaceId: facilityId,
      workerId: agentId,
    });
  };

  const handleRouteToChat = async () => {
    try {
      await createChatChannel({
        facilityId,
        agentId,
      });

      const prefilledMessage = "I'm interested in scheduling an orientation shift.";
      const pathname = generatePath(CHAT_PAGE_FULL_PATH, { facilityId });
      const params = new URLSearchParams({ prefilledMessage });
      history.push({ pathname, search: `?${params.toString()}` });

      logEvent(APP_V2_USER_EVENTS.ORIENTATION_FACILITY_CHAT_TAPPED, {
        shiftId,
        workplaceId: facilityId,
        workerId: agentId,
      });
    } catch {
      showErrorToast("Failed to create chat channel. Please try again.");
    }
  };

  const { data: upcomingShiftsResponse, isLoading: isLoadingUpcomingShifts } = useFutureShifts(
    worker.tmz ?? "",
    { enabled: isDefined(worker.tmz) }
  );

  const upcomingShifts = upcomingShiftsResponse?.response ?? [];
  const upcomingOrientationAtWorkplace = upcomingShifts.some(
    (shift) => isDefined(shift.orientationBlockId) && shift.facility.userId === facilityId
  );

  const { data: orientationShiftBlocksData, isLoading: isOrientationShiftBlocksLoading } =
    useFetchPaginatedShiftBlocks(
      {
        filter: {
          agentId,
          facilityId,
          qualification: shiftQualification,
          maxDistanceMiles: enableDistanceFiltering ? filters.distance.toString() : undefined,
          isOrientationBlock: true,
        },
      },
      {
        enabled: isDefined(shiftQualification),
        meta: {
          userErrorMessage: "Something went wrong while loading orientation shifts",
        },
      }
    );

  const isLoading =
    isCreatingChatChannel || isOrientationShiftBlocksLoading || isLoadingUpcomingShifts;

  const orientationShiftBlockCount = orientationShiftBlocksData?.pages?.[0]?.data.length ?? 0;

  const workplaceHasUpcomingOrientation =
    !isOrientationShiftBlocksLoading && orientationShiftBlockCount > 0;

  const handleClick = workplaceHasUpcomingOrientation
    ? handleRouteToShiftBlocksView
    : handleRouteToChat;

  const buttonText = getButtonText({
    shiftHasAdditionalUnmetBookabilityCriteria,
    upcomingOrientationAtWorkplace,
    workplaceHasUpcomingOrientation,
  });
  const description = getDescription({
    shiftHasAdditionalUnmetBookabilityCriteria,
    upcomingOrientationAtWorkplace,
    workplaceHasUpcomingOrientation,
  });

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      intent="neutral"
      title="Orientation required"
      description={description}
      illustrationType="unlock"
      footer={
        isDefined(buttonText) && (
          <LoadingButton
            fullWidth
            variant="contained"
            size="medium"
            isLoading={isLoading}
            onClick={handleClick}
          >
            {buttonText}
          </LoadingButton>
        )
      }
    />
  );
}
